import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Plus, Search, Edit2, Trash2, X, Building, Phone, Mail,
  MapPin, AlertCircle, Loader2, CheckCircle, Ban,
} from 'lucide-react';
import { masterDataAPI } from '../services/master-data-api.service';
import { Contractor } from '../types/master-data.types';

type VendorForm = Partial<Contractor>;

const EMPTY_FORM: VendorForm = {
  contractor_name: '', pan_number: '', firm_name: '', gstin: '',
  contact_number: '', email: '', address: '', city: '', state: 'Telangana',
  pincode: '', contractor_class: 'Class I', registration_number: '',
  bank_name: '', account_number: '', ifsc_code: '', work_capacity: undefined,
  performance_rating: undefined, specialization: '', remarks: '', is_active: true,
};

const CLASS_OPTIONS = ['Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Special Class'];

const VendorsManagement: React.FC = () => {
  const [vendors, setVendors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<VendorForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Contractor | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await masterDataAPI.getContractors();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(v =>
      [v.contractor_name, v.firm_name, v.pan_number, v.city, v.contractor_class, v.contact_number]
        .filter(Boolean).some(s => String(s).toLowerCase().includes(q)),
    );
  }, [vendors, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (v: Contractor) => {
    setEditingId(v.contractor_id);
    setForm({ ...v });
    setFormError(null);
    setShowModal(true);
  };

  const setField = (k: keyof VendorForm, val: any) => setForm(prev => ({ ...prev, [k]: val }));

  const handleSave = async () => {
    if (!form.contractor_name?.trim()) { setFormError('Vendor / contractor name is required'); return; }
    if (!form.pan_number?.trim()) { setFormError('PAN number is required'); return; }
    try {
      setSaving(true);
      setFormError(null);
      // Strip empty strings → undefined so optional fields validate cleanly.
      const payload: VendorForm = {};
      (Object.keys(form) as (keyof VendorForm)[]).forEach((k) => {
        const val = form[k];
        if (val !== '' && val !== undefined && val !== null) (payload as any)[k] = val;
      });
      // Numeric coercion
      ['work_capacity', 'performance_rating', 'concurrent_works_limit', 'experience_years'].forEach((k) => {
        if (payload[k as keyof VendorForm] !== undefined) {
          (payload as any)[k] = Number(payload[k as keyof VendorForm]);
        }
      });

      if (editingId) {
        await masterDataAPI.updateContractor(editingId, payload);
        setNotice('Vendor updated successfully');
      } else {
        await masterDataAPI.createContractor(payload);
        setNotice('Vendor created successfully');
      }
      setShowModal(false);
      await loadVendors();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await masterDataAPI.deleteContractor(deleteTarget.contractor_id);
      setNotice('Vendor deactivated successfully');
      setDeleteTarget(null);
      await loadVendors();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete vendor');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center">
          <Users className="w-7 h-7 text-blue-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vendors / Agencies</h1>
            <p className="text-sm text-gray-500">Manage contractors, vendors and executing agencies</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" /> Add Vendor
        </button>
      </div>

      {notice && (
        <div className="mb-4 flex items-center bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" /> {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" /> {error}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, firm, PAN, city…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading vendors…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Building className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            {search ? 'No vendors match your search.' : 'No vendors yet. Click “Add Vendor” to create one.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Vendor / Firm', 'PAN', 'Class', 'Contact', 'Location', 'Rating', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((v) => (
                  <tr key={v.contractor_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 flex items-center">
                        {v.contractor_name}
                        {v.is_blacklisted && (
                          <span className="ml-2 inline-flex items-center text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                            <Ban className="w-3 h-3 mr-1" /> Blacklisted
                          </span>
                        )}
                      </div>
                      {v.firm_name && <div className="text-gray-500 text-xs">{v.firm_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">{v.pan_number}</td>
                    <td className="px-4 py-3 text-gray-700">{v.contractor_class || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {v.contact_number && <div className="flex items-center text-xs"><Phone className="w-3 h-3 mr-1 text-gray-400" />{v.contact_number}</div>}
                      {v.email && <div className="flex items-center text-xs text-gray-500"><Mail className="w-3 h-3 mr-1 text-gray-400" />{v.email}</div>}
                      {!v.contact_number && !v.email && '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {(v.city || v.state)
                        ? <span className="flex items-center text-xs"><MapPin className="w-3 h-3 mr-1 text-gray-400" />{[v.city, v.state].filter(Boolean).join(', ')}</span>
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{v.performance_rating != null ? `${v.performance_rating}/10` : '—'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(v)} title="Edit"
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteTarget(v)} title="Deactivate"
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2">{filtered.length} vendor(s){search ? ` (filtered from ${vendors.length})` : ''}</p>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Vendor' : 'Add Vendor'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" /> {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Vendor / Contractor Name *">
                  <input className={inp} value={form.contractor_name || ''} onChange={(e) => setField('contractor_name', e.target.value)} />
                </Field>
                <Field label="PAN Number *">
                  <input className={inp} value={form.pan_number || ''} onChange={(e) => setField('pan_number', e.target.value.toUpperCase())} maxLength={10} />
                </Field>
                <Field label="Firm / Company">
                  <input className={inp} value={form.firm_name || ''} onChange={(e) => setField('firm_name', e.target.value)} />
                </Field>
                <Field label="GSTIN">
                  <input className={inp} value={form.gstin || ''} onChange={(e) => setField('gstin', e.target.value.toUpperCase())} maxLength={15} />
                </Field>
                <Field label="Contractor Class">
                  <select className={inp} value={form.contractor_class || ''} onChange={(e) => setField('contractor_class', e.target.value)}>
                    <option value="">—</option>
                    {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Registration Number">
                  <input className={inp} value={form.registration_number || ''} onChange={(e) => setField('registration_number', e.target.value)} />
                </Field>
                <Field label="Contact Number">
                  <input className={inp} value={form.contact_number || ''} onChange={(e) => setField('contact_number', e.target.value)} />
                </Field>
                <Field label="Email">
                  <input className={inp} value={form.email || ''} onChange={(e) => setField('email', e.target.value)} />
                </Field>
                <Field label="City">
                  <input className={inp} value={form.city || ''} onChange={(e) => setField('city', e.target.value)} />
                </Field>
                <Field label="State">
                  <input className={inp} value={form.state || ''} onChange={(e) => setField('state', e.target.value)} />
                </Field>
                <Field label="Pincode">
                  <input className={inp} value={form.pincode || ''} onChange={(e) => setField('pincode', e.target.value)} />
                </Field>
                <Field label="Work Capacity (₹ lakhs)">
                  <input type="number" className={inp} value={form.work_capacity ?? ''} onChange={(e) => setField('work_capacity', e.target.value)} />
                </Field>
                <Field label="Performance Rating (0–10)">
                  <input type="number" step="0.1" min="0" max="10" className={inp} value={form.performance_rating ?? ''} onChange={(e) => setField('performance_rating', e.target.value)} />
                </Field>
                <Field label="Bank Name">
                  <input className={inp} value={form.bank_name || ''} onChange={(e) => setField('bank_name', e.target.value)} />
                </Field>
                <Field label="Account Number">
                  <input className={inp} value={form.account_number || ''} onChange={(e) => setField('account_number', e.target.value)} />
                </Field>
                <Field label="IFSC Code">
                  <input className={inp} value={form.ifsc_code || ''} onChange={(e) => setField('ifsc_code', e.target.value.toUpperCase())} />
                </Field>
              </div>
              <Field label="Address">
                <textarea className={inp} rows={2} value={form.address || ''} onChange={(e) => setField('address', e.target.value)} />
              </Field>
              <Field label="Specialization / Remarks">
                <input className={inp} value={form.specialization || ''} onChange={(e) => setField('specialization', e.target.value)} />
              </Field>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Create Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Deactivate Vendor</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Deactivate <span className="font-medium">{deleteTarget.contractor_name}</span>? They will no longer appear in
              selection lists. This is blocked if the vendor is still assigned to active works.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default VendorsManagement;
